import { useQuery } from '@apollo/react-hooks';
import { ff } from '@atlassian/jira-feature-flagging';
import { fg } from '@atlassian/jira-feature-gating';
import { PARENT_HIERARCHY_TYPE } from '@atlassian/jira-issue-type-hierarchies';
import { EPIC_QUERY } from '@atlassian/jira-software-backlog-panel/src/services/epic/get';
import type {
	EpicQueryResponse,
	EpicQueryVariables,
} from '@atlassian/jira-software-backlog-panel/src/services/epic/get/types.tsx';
import { isEpicPanelInProgressBarTooltipsUpdateEnabled } from '../../../feature-flags';
import { DEFAULT_PARENT_ISSUE_TYPE_NAME } from '../../../model/constants';
import { useBacklogSelector } from '../../../state';
import { getFirstLevelHierarchyName } from '../../../state/selectors/issue-hierarchy';
import { useIsCMPBacklog } from '../../../state/state-hooks/capabilities';
import type { UseGetEpicsAndProject } from './types';

const useEpicQuery = (boardId: number, isCompanyManaged: boolean, shouldSyncEpics: boolean) =>
	useQuery<EpicQueryResponse, EpicQueryVariables>(EPIC_QUERY, {
		skip: __SERVER__,
		variables: {
			boardId,
			// Legacy BacklogGetAllEpics does not require isCompanyManaged variables
			// Therefore, we omit isCompanyManaged variable if FF is off
			isCompanyManaged,
			isQueryColumns: isCompanyManaged && isEpicPanelInProgressBarTooltipsUpdateEnabled(),
			skipEpicDetails: ff('jsw-epic-panel-performance-async-epic-details'),
			includeIssueTypes: fg('jsw_cmp_backlog_remove_epics_request'),
		},
		context: {
			headers: {
				/*  This header is required for EpicPanelEpics query
                        Please note that isCompanyManaged == true is also behind feature flag
                */
				'is-company-managed-board': isCompanyManaged,
			},
		},
		fetchPolicy: shouldSyncEpics ? 'cache-and-network' : 'cache-first',
	});

/**
 * @deprecated use `useEpicsFilterData` from '@atlassian/jira-backlog-epics-store' instead.
 * Should be removed with 'jsw_epic_panel_paginated_fetch' cleanup
 */
export const useGetEpicsAndProject = (
	boardId: number,
	shouldSyncEpics = false,
): UseGetEpicsAndProject => {
	const isCompanyManaged = useIsCMPBacklog();
	const firstLevelHierarchyName = useBacklogSelector(getFirstLevelHierarchyName);

	let isParentIssueTypeAvailable = false;
	let issueParentsTypeName: string = DEFAULT_PARENT_ISSUE_TYPE_NAME;

	const { data } = useEpicQuery(boardId, isCompanyManaged, shouldSyncEpics);

	if (__SERVER__ || !data) {
		return {
			parents: [],
			isParentIssueTypeAvailable,
			issueParentsTypeName,
		};
	}

	const {
		boardScope: { projectLocation },
		globalConfig,
	} = data;

	if (isCompanyManaged) {
		isParentIssueTypeAvailable =
			globalConfig?.issueParentTypeConfig.hierarchyLevelType === PARENT_HIERARCHY_TYPE;

		issueParentsTypeName =
			firstLevelHierarchyName ||
			globalConfig?.issueParentTypeConfig.name ||
			DEFAULT_PARENT_ISSUE_TYPE_NAME;
	} else if (projectLocation?.issueTypes.length) {
		const issueType = projectLocation.issueTypes.find(
			({ hierarchyLevelType }) => hierarchyLevelType === PARENT_HIERARCHY_TYPE,
		);

		isParentIssueTypeAvailable = Boolean(issueType);

		if (issueType) {
			issueParentsTypeName = issueType.name;
		}
	}

	return {
		parents: data.boardScope.issueParents,
		isParentIssueTypeAvailable,
		issueParentsTypeName,
	};
};
